import { Location, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { Router, NavigationEnd, ActivatedRoute, Data } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { buildDocumentTitle } from 'src/app/core/branding/public-brand';
import { AdminNavigationHistoryService } from '@core/shell/admin-navigation-history.service';
import {
  canShowAdminBack,
  normalizeAdminPath,
  resolveAdminBackTarget,
} from '@core/shell/admin-page-back.utils';
import { AdminPageBarService } from '../admin-page-bar/admin-page-bar.service';
import {
  AdminPageBarAction,
  AdminPageBarRouteData,
} from '../admin-page-bar/admin-page-bar.models';

@Component({
  selector: 'app-breadcrumb',
  imports: [
    RouterModule,
    NgTemplateOutlet,
    MatCardModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslateModule,
    TablerIconsModule,
  ],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class AppBreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly translate = inject(TranslateService);
  private readonly pageBarService = inject(AdminPageBarService);
  private readonly navigationHistory = inject(AdminNavigationHistoryService);

  pageInfo: AdminPageBarRouteData = {};
  private lastPageBarPath = '';

  readonly actions = this.pageBarService.actions;
  readonly titleOverride = this.pageBarService.titleOverride;
  readonly subtitleOverride = this.pageBarService.subtitleOverride;
  readonly leadingExtra = this.pageBarService.leadingExtra;
  readonly backOverride = this.pageBarService.backOverride;

  readonly visibleActions = computed(() => this.actions().filter((action) => !action.hidden));

  readonly showEndCluster = computed(
    () => !!this.leadingExtra() || this.visibleActions().length > 0,
  );

  /** Desktop: actions that stay as buttons (not `menu: true`). */
  readonly desktopBarActions = computed(() =>
    this.visibleActions().filter((action) => !action.menu),
  );

  /** Desktop overflow for `menu: true` actions. */
  readonly desktopMenuActions = computed(() =>
    this.visibleActions().filter((action) => !!action.menu),
  );

  readonly mobilePrimaryAction = computed(() => {
    const visible = this.visibleActions();
    const barPreferred = visible.filter((action) => !action.menu);
    const pool = barPreferred.length > 0 ? barPreferred : visible;
    return pool.find((action) => action.variant === 'primary') ?? pool[0] ?? null;
  });

  readonly mobileMenuActions = computed(() => {
    const visible = this.visibleActions();
    const primary = this.mobilePrimaryAction();
    if (!primary || visible.length <= 1) {
      return [];
    }
    return visible.filter((action) => action.id !== primary.id);
  });

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map(() => this.activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(filter((route) => route.outlet === 'primary'))
      .pipe(mergeMap((route) => route.data))
      .subscribe((event: Data) => {
        const data = event as AdminPageBarRouteData;
        this.titleService.setTitle(buildDocumentTitle(this.resolveTitle(data)));

        const path = normalizeAdminPath(this.router.url);
        const pathChanged = path !== this.lastPageBarPath;
        this.lastPageBarPath = path;

        if (!data.pageBar) {
          this.pageBarService.clear();
        } else if (pathChanged) {
          this.pageBarService.setTitleOverride(null);
          this.pageBarService.setSubtitleOverride(null);
          this.pageBarService.setBackOverride(null);
          this.pageBarService.clearStaleMyPageActions();
        }

        this.pageInfo = data;
      });
  }

  titleText(): string {
    const override = this.pageBarService.titleOverride();
    if (override) {
      return override;
    }
    if (typeof this.pageInfo.titleKey === 'string' && this.pageInfo.titleKey.trim()) {
      return this.translate.instant(this.pageInfo.titleKey);
    }
    return this.pageInfo.title ?? '';
  }

  subtitleText(): string | null {
    const override = this.pageBarService.subtitleOverride();
    if (override) {
      return override;
    }
    if (typeof this.pageInfo.subtitle === 'string' && this.pageInfo.subtitle.trim()) {
      return this.pageInfo.subtitle;
    }
    if (typeof this.pageInfo.subtitleKey === 'string' && this.pageInfo.subtitleKey.trim()) {
      return this.translate.instant(this.pageInfo.subtitleKey);
    }
    return null;
  }

  private backNavigationInput() {
    return {
      currentUrl: this.router.url,
      routeData: this.pageInfo,
      previousInAppUrl: this.navigationHistory.previousInAppUrl(),
    };
  }

  showBack(): boolean {
    if (this.backOverride()) {
      return true;
    }
    return canShowAdminBack(this.backNavigationInput());
  }

  backAriaLabel(): string {
    const overrideTitle = this.backOverride()?.title?.trim();
    if (overrideTitle) {
      return this.translate.instant('layout.backTo', { name: overrideTitle });
    }
    const parentTitle = this.breadcrumbParentTitle();
    if (parentTitle) {
      return this.translate.instant('layout.backTo', { name: parentTitle });
    }
    return this.translate.instant('layout.back');
  }

  private breadcrumbParentTitle(): string | null {
    const urls = this.pageInfo.urls ?? [];
    if (urls.length <= 1) {
      return null;
    }
    const parent = urls[urls.length - 2];
    const title = parent?.title?.trim();
    return title || null;
  }

  goBack(): void {
    const override = this.backOverride()?.url?.trim();
    if (override) {
      void this.router.navigateByUrl(override);
      return;
    }
    const target = resolveAdminBackTarget(this.backNavigationInput());
    if (target) {
      void this.router.navigateByUrl(target);
      return;
    }
    this.location.back();
  }

  actionLabel(action: AdminPageBarAction): string {
    if (action.label) {
      return action.label;
    }
    if (action.labelKey) {
      return this.translate.instant(action.labelKey);
    }
    return '';
  }

  /** Iconify ids llevan `prefix:name` (p. ej. `tabler:cash-register`). */
  isIconifyActionIcon(icon: string | undefined): boolean {
    return !!icon && icon.includes(':');
  }

  actionButtonClass(action: AdminPageBarAction): string {
    switch (action.variant) {
      case 'secondary':
        return 'milinco-btn-secondary';
      case 'outline':
        return 'milinco-btn-outline';
      case 'primary':
      default:
        return 'milinco-btn-primary';
    }
  }

  runAction(action: AdminPageBarAction): void {
    if (action.disabled || action.hidden) {
      return;
    }
    action.handler();
  }

  private resolveTitle(data: AdminPageBarRouteData): string | null {
    const override = this.pageBarService.titleOverride();
    if (override) {
      return override;
    }
    if (typeof data.titleKey === 'string' && data.titleKey.trim()) {
      return this.translate.instant(data.titleKey);
    }
    return typeof data.title === 'string' ? data.title : null;
  }
}
